import { useRead, ReadState, isCreators } from "@/components/LoadingStates";
import EmptyState from "@/components/EmptyState";
import LiveDemoBadge, { isLiveCreator } from "@/components/LiveDemoBadge";
import MediaImage from "@/components/MediaImage";

import { useSearchParams, useLocation, useNavigate, Link } from "react-router-dom";


import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PlatformBadge from "@/components/PlatformBadge";
import CompareColumn from "@/components/CompareColumn";
import ShareButton from "@/components/ShareButton";
import ExportButton from "@/components/ExportButton";
import { GitCompare, ArrowLeftRight } from "lucide-react";
import { formatNumber } from "@/lib/format";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const ROWS = [
  { key: "videos", label: "Videos tracked", fmt: (v) => v },
  { key: "cadence_per_week", label: "Cadence / week", fmt: (v) => v },
  { key: "avg_views", label: "Avg views", fmt: formatNumber },
  { key: "median_views", label: "Median views", fmt: formatNumber },
  { key: "peak_views", label: "Peak views", fmt: formatNumber },
  { key: "outliers", label: "Outliers", fmt: (v) => v },
  { key: "outlier_rate_pct", label: "Outlier rate", fmt: (v) => `${v}%` },
  { key: "peak_multiplier", label: "Peak multiplier", fmt: (v) => `${v}x` },
  { key: "avg_engagement_pct", label: "Avg engagement", fmt: (v) => `${v}%` },
  { key: "views_per_follower_pct", label: "Views / follower", fmt: (v) => `${v}%` },
  { key: "avg_duration_s", label: "Avg duration", fmt: (v) => `${v}s`, neutral: true },
  // compare.py best_day counts publications per weekday; it is not a performance measure.
  { key: "best_day", label: "Jour le plus publié", fmt: (v) => v || "—", neutral: true },
];

export default function Compare() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const setParams = (p) => navigate({ pathname: location.pathname, search: `?${p}`, hash: location.hash });
  const a = params.get("a") || "";
  const b = params.get("b") || "";
  const creatorsRead = useRead("/creators", isCreators);
  const creators = creatorsRead.data || [];
  const validPair = !!a && !!b && a !== b;
  const compareRead = useRead(validPair ? `/creators/compare?${new URLSearchParams({ a, b })}` : null, (d) => d && [d.a, d.b].every((p) => p && p.creator && typeof p.creator.name === "string" && p.stats && typeof p.stats.videos === "number" && ["series", "weekday", "hook_mix", "top_videos"].every((k) => Array.isArray(p[k]) && p[k].every((item) => item && typeof item === "object"))));
  const data = compareRead.status === "success" && validPair ? compareRead.data : null;
  const set = (k, v) => { const p = new URLSearchParams(params); p.set(k, v); setParams(p); };
  const swap = () => { const p = new URLSearchParams(params); p.set("a", b); p.set("b", a); setParams(p); };

  const series = data ? data.a.series.map((w, i) => ({ week: w.week, a: w.views, b: data.b.series[i]?.views || 0 })) : [];

  return (
    <div className="space-y-6" data-testid="compare-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <GitCompare className="h-6 w-6 text-primary" />
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Creator Compare</h1>
          </div>
          <p className="text-muted-foreground text-sm mt-1">Chaque profil inclut ses collaborations attestées. Les deux colonnes peuvent partager des vidéos et ne sont pas additives.</p>
        </div>
        {data && a && b && (
          <div className="flex items-center gap-2">
            <ExportButton path={`/export/compare.csv?a=${a}&b=${b}`} testid="export-compare-button" />
            <ShareButton kind="compare" params={{ a, b }} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
        <ReadState read={creatorsRead} scope="compare-creators"><CreatorPicker value={a} onChange={(v) => set("a", v)} creators={creators} exclude={b} testid="compare-select-a" />
        <button onClick={swap} disabled={!a || !b} className="mx-auto h-10 w-10 rounded-full border border-border bg-card flex items-center justify-center hover:bg-accent transition-colors disabled:opacity-40" aria-label="Swap" data-testid="compare-swap">
          <ArrowLeftRight className="h-4 w-4" />
        </button>
        <CreatorPicker value={b} onChange={(v) => set("b", v)} creators={creators} exclude={a} testid="compare-select-b" /></ReadState>
      </div>

      {!validPair ? <EmptyState testid={a && b ? "compare-unavailable" : "compare-empty"} title={a && b ? "Choisissez deux créateurs distincts" : "Sélectionnez deux créateurs à comparer"} primaryCta={{ label: "Consulter Creators", to: "/creators", testid: "compare-empty-cta" }} /> : <ReadState read={compareRead} scope="compare">
        {data && <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[data.a, data.b].map((p, i) => (
              <Card key={p.creator.id} className="p-4 flex items-center gap-3" data-testid={`compare-header-${i === 0 ? "a" : "b"}`}>
                <MediaImage src={p.creator.avatar} alt="" name={p.creator.name} kind="avatar" demo={!isLiveCreator(p.creator)} className="h-12 w-12 rounded-full object-cover" />
                <div className="min-w-0 flex-1">
                  <Link to={`/creators/${p.creator.id}`} className="font-semibold truncate block hover:underline">{p.creator.name}</Link>
                  <div className="text-xs text-muted-foreground truncate">{p.creator.handle} · {p.creator.followers_available === false ? "Abonnés indisponibles" : `${formatNumber(p.creator.followers)} followers`}</div>
                </div>
                <PlatformBadge platform={p.creator.platform} size="xs" />
                <LiveDemoBadge creator={p.creator} />
              </Card>
            ))}
          </div>

          <Card className="p-5" data-testid="compare-stats">
            <h3 className="font-display font-bold text-lg mb-3">Scorecard</h3>
            <div className="divide-y divide-border">
              {ROWS.map((r) => {
                const va = data.a.stats[r.key], vb = data.b.stats[r.key];
                const winA = !r.neutral && typeof va === "number" && typeof vb === "number" && va > vb;
                const winB = !r.neutral && typeof vb === "number" && typeof va === "number" && vb > va;
                return (
                  <div key={r.key} className="grid grid-cols-[1fr_auto_1fr] items-center py-2 text-sm" data-testid={`compare-row-${r.key}`}>
                    <div className={`font-mono font-bold text-right pr-4 ${winA ? "text-[hsl(var(--emerald))]" : ""}`}>{va == null ? "Indisponible" : r.fmt(va)}</div>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground text-center w-36">{r.label}</div>
                    <div className={`font-mono font-bold pl-4 ${winB ? "text-[hsl(var(--emerald))]" : ""}`}>{vb == null ? "Indisponible" : r.fmt(vb)}</div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="font-display font-bold text-lg mb-1">Weekly views · last 8 weeks</h3>
            <p className="text-xs text-muted-foreground mb-4">Who's trending up?</p>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ left: -8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="week" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={formatNumber} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v) => formatNumber(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="a" name={data.a.creator.name} stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="b" name={data.b.creator.name} stroke="hsl(var(--rose))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CompareColumn profile={data.a} side="a" color="primary" />
            <CompareColumn profile={data.b} side="b" color="rose" />
          </div>
        </>}
      </ReadState>}
    </div>
  );
}

function CreatorPicker({ value, onChange, creators, exclude, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-11" data-testid={testid}><SelectValue placeholder="Choose a creator…" /></SelectTrigger>
      <SelectContent>
        {creators.filter((c) => c.id !== exclude).map((c) => (
          <SelectItem key={c.id} value={c.id} data-testid={`${testid}-option-${c.id}`}>{c.name} · {c.handle}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
